/**
 * DEPLETIONS-INVENTORY MODULE
 * Self-contained. Import only from /shared or within this module.
 * Do not import from other modules directly.
 */

// brandedExport.js — a branded native-.xlsx export that matches the Sales
// Forecast export: company/team logo at the top, title + (bold) subtitle,
// export date, who ran it, the active filters, and a Brand-Command-styled grid
// (navy headers, alternating rows, a bold totals row). ExcelJS is loaded on
// demand so it stays out of the main bundle.

import { supabase } from "../../../shared/utils/supabaseClient.js";
import { getMyPrimaryAccount } from "../../../shared/utils/accountService.js";

// ARGB palette (Excel wants AARRGGBB) — identical to the forecast export.
// ORANGE follows the app (backlog 145/166), using the LIGHT-theme values since
// a spreadsheet is always a light surface: `accent` is the TEXT role (#8F5408,
// 4.57:1 on white — the fill orange would be 1.96:1 and unreadable) and
// `accentBar` is the solid divider, which carries no text.
const C = {
  navy: "FF1B2A4A", band: "FF2E4A72", accent: "FF8F5408", accentBar: "FFF2A33C",
  border: "FFD4DCEA", white: "FFFFFFFF", text: "FF1B2A4A",
  muted: "FF6B7A99", altRow: "FFFBFCFE", totalBg: "FFF1F5FB",
};
const THIN = { style: "thin", color: { argb: C.border } };
const ALL_BORDERS = { top: THIN, left: THIN, bottom: THIN, right: THIN };

// Backlog 330: an export with no usable tenant logo goes out UNBRANDED — we
// never substitute our own mark. Say so in the export dialog; the user is about
// to send this sheet to a distributor.
export const LOGO_NOTICE = {
  none: "Exported without a logo — your company hasn't uploaded one yet. Add it in System Settings so your reports carry your brand.",
  failed: "Exported without a logo — your company logo could not be loaded. Everything else in the document is complete.",
};

// ── Logo helpers ─────────────────────────────────────────────────────────────
function fetchAsDataUrl(url) {
  return fetch(url, { mode: "cors" })
    .then((r) => { if (!r.ok) throw new Error(`logo ${r.status}`); return r.blob(); })
    .then((blob) => new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = reject;
      fr.readAsDataURL(blob);
    }));
}

// The account logo, or null. Backlog 194/330: this used to fall back to the
// Brand Command mark whenever the tenant had no logo or the fetch failed, which
// put OUR logo on a depletions/inventory sheet the tenant sends to a
// distributor — their vendor's brand on their document. A missing or
// unfetchable logo now means no logo at all, and the caller is told so it can
// warn.
async function resolveLogo(logoUrl) {
  if (!logoUrl) return null;
  try { return await fetchAsDataUrl(logoUrl); } catch { return null; }
}

// Rasterize ANY logo data-URL (svg/png/jpg/webp) to a fitted PNG — embedding the
// original bytes is what triggers Excel's "this picture can't be displayed".
function rasterizeToPng(dataUrl, maxW, maxH) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const nw = img.naturalWidth || maxW, nh = img.naturalHeight || maxH;
      const scale = Math.min(maxW / nw, maxH / nh, 1);
      const width = Math.max(1, Math.round(nw * scale));
      const height = Math.max(1, Math.round(nh * scale));
      try {
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve({ base64: canvas.toDataURL("image/png").split(",")[1], width, height });
      } catch { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

async function actorName() {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    return user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email || "—";
  } catch { return "—"; }
}

/**
 * @param {Object} opts
 * @param {string}   opts.title       e.g. "Depletion Data"
 * @param {string}   [opts.subtitle]  bold accent line, e.g. "Year: 2025"
 * @param {Array}    [opts.filters]   [[label, value], …] (only truthy values)
 * @param {Array}    opts.columns     [{ label, key, kind:'dim'|'num', width? }]
 * @param {Array}    opts.rows        array of objects keyed by column.key
 * @param {Object}   [opts.totals]    object keyed by column.key for a bold totals row
 * @param {string}   opts.sheetName   worksheet name + filename slug
 * @param {string}   [opts.note]      footer note
 * @param {Object}   [opts.chart]     optional 2nd worksheet: { name, base64, width, height }
 * @returns {Promise<{ logo: 'ok'|'none'|'failed' }>}
 *   `logo` lets the caller warn in the export dialog when the workbook went out
 *   with no mark on it (backlog 330).
 */
export async function exportBranded({ title, subtitle, filters = [], columns, rows, totals, sheetName = "Export", note, chart }) {
  const ExcelJS = (await import("exceljs")).default;

  let teamName = "", logoUrl = null, acctFailed = false;
  try { const acct = await getMyPrimaryAccount(); teamName = acct?.name ?? ""; logoUrl = acct?.logoUrl ?? null; } catch (e) { acctFailed = true; /* unbranded */ }
  const logoData = await resolveLogo(logoUrl);
  const who = await actorName();

  const now = new Date();
  const dateStr = now.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const timeStr = now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

  const wb = new ExcelJS.Workbook();
  wb.creator = "Brand Command";
  wb.created = now;
  const ws = wb.addWorksheet(sheetName, { views: [{ showGridLines: false }] });

  const colCount = columns.length;
  columns.forEach((c, i) => { ws.getColumn(i + 1).width = c.width || (c.kind === "num" ? 13 : 18); });

  const mergeRow = (r, text, style = {}) => {
    ws.mergeCells(r, 1, r, colCount);
    const cell = ws.getCell(r, 1);
    cell.value = text;
    cell.font = style.font || {};
    if (style.fill) cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: style.fill } };
    cell.alignment = style.alignment || { vertical: "middle", horizontal: "left" };
    if (style.height) ws.getRow(r).height = style.height;
  };

  // ── Branded header ──
  // The logo row only exists when there IS a tenant logo — with no mark to
  // place, the sheet starts at the title rather than leaving a 46pt blank band
  // where ours used to sit (backlog 330).
  let logoStatus = logoUrl || acctFailed ? "failed" : "none";
  let r = 1;
  if (logoData) {
    try {
      const png = await rasterizeToPng(logoData, 230, 48);
      if (png) {
        ws.getRow(1).height = 46;
        const imgId = wb.addImage({ base64: png.base64, extension: "png" });
        ws.addImage(imgId, { tl: { col: 0, row: 0 }, ext: { width: png.width, height: png.height } });
        logoStatus = "ok";
        r = 2;
      }
    } catch (e) { /* skip */ }
  }

  mergeRow(r++, title, { font: { name: "Inter", size: 16, bold: true, color: { argb: C.navy } } });
  if (subtitle) mergeRow(r++, subtitle, { font: { name: "Inter", size: 13, bold: true, color: { argb: C.accent } } });
  if (teamName) mergeRow(r++, teamName, { font: { name: "Inter", size: 12, bold: true, color: { argb: C.navy } } });
  mergeRow(r++, "", { fill: C.accentBar, height: 4 });
  mergeRow(r++, `Exported: ${dateStr} at ${timeStr}     ·     Exported by: ${who}`, { font: { name: "Inter", size: 10, color: { argb: C.muted } } });
  if (filters.length) {
    mergeRow(r++, "Filters — " + filters.map(([k, v]) => `${k}: ${v}`).join("   ·   "), { font: { name: "Inter", size: 10, italic: true, color: { argb: C.muted } } });
  }
  ws.getRow(r++).height = 6;

  // ── Table header ──
  const headerRow = r;
  columns.forEach((c, i) => {
    const cell = ws.getCell(r, i + 1);
    cell.value = c.label;
    cell.font = { name: "Inter", size: 11, bold: true, color: { argb: C.white } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.navy } };
    cell.alignment = { vertical: "middle", horizontal: c.kind === "num" ? "right" : "left" };
    cell.border = ALL_BORDERS;
  });
  r++;
  ws.views = [{ state: "frozen", xSplit: 0, ySplit: headerRow, showGridLines: false }];

  // ── Data rows ──
  rows.forEach((row, i) => {
    const bg = i % 2 ? null : C.altRow;
    columns.forEach((c, ci) => {
      const cell = ws.getCell(r, ci + 1);
      const v = row[c.key];
      if (c.kind === "num") {
        cell.value = v === "" || v == null ? null : Number(v);
        cell.numFmt = c.numFmt || "#,##0";
        cell.alignment = { horizontal: "right" };
      } else {
        cell.value = v ?? "";
        cell.alignment = { horizontal: "left" };
      }
      cell.font = { name: "Inter", size: 10, bold: c.kind === "dim" && ci === 0, color: { argb: C.text } };
      if (bg) cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bg } };
      cell.border = ALL_BORDERS;
    });
    r++;
  });

  if (!rows.length) {
    mergeRow(r++, "No data for the selected filters.", { font: { name: "Inter", size: 11, color: { argb: C.muted } }, alignment: { horizontal: "center" } });
  }

  // ── Totals row ──
  if (totals) {
    columns.forEach((c, ci) => {
      const cell = ws.getCell(r, ci + 1);
      const v = totals[c.key];
      if (c.kind === "num") { cell.value = v === "" || v == null ? null : Number(v); cell.numFmt = c.numFmt || "#,##0"; cell.alignment = { horizontal: "right" }; }
      else { cell.value = v ?? (ci === 0 ? "Total" : ""); cell.alignment = { horizontal: "left" }; }
      cell.font = { name: "Inter", size: 11, bold: true, color: { argb: C.white } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.navy } };
      cell.border = ALL_BORDERS;
    });
    r++;
  }

  if (note) mergeRow(r++, note, { font: { name: "Inter", size: 9, italic: true, color: { argb: C.muted } } });

  // ── Optional chart worksheet (2nd tab) ──
  if (chart && chart.base64) {
    const cs = wb.addWorksheet(chart.name || "Chart", { views: [{ showGridLines: false }] });
    cs.getColumn(1).width = 3;
    try {
      const chartId = wb.addImage({ base64: chart.base64, extension: "png" });
      cs.addImage(chartId, { tl: { col: 1, row: 1 }, ext: { width: chart.width || 920, height: chart.height || 470 } });
    } catch (e) { /* skip chart on failure */ }
  }

  // ── Download ──
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const team = (teamName || "team").trim() || "team";
  const filename = `${team}_${sheetName}_${now.toISOString().slice(0, 10)}.xlsx`.replace(/[^A-Za-z0-9._-]+/g, "_");
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);

  return { logo: logoStatus };
}
